/**
 * PWWCapital — Session Warning Listener
 *
 * Listens for the `pww:session-warning` DOM event dispatched by AuthContext
 * and shows a persistent warning toast instructing the user that their session
 * will expire soon. The toast is dismissed automatically once the user acts
 * (the session timeout hook resets on any interaction).
 *
 * Mounted once inside App, above the router, so it always runs regardless
 * of which page is active.
 */
import { useEffect, useRef } from 'react';
import { useToast } from '../../context/ToastContext';

function SessionWarningListener() {
  const { toast } = useToast();
  const warningIdRef = useRef(null);
  const dismissedRef = useRef(false);

  useEffect(() => {
    function handleWarning(e) {
      if (dismissedRef.current) return;
      const sec = e.detail?.secondsLeft ?? 60;
      const mins = Math.ceil(sec / 60);

      // Dismiss any previous warning
      if (warningIdRef.current) {
        toast.dismiss(warningIdRef.current);
      }

      warningIdRef.current = toast.warning(
        `Your session will expire in ${mins} minute${mins !== 1 ? 's' : ''}. Move your mouse or type to stay signed in.`,
        {
          title: 'Session Expiring Soon',
          duration: sec * 1000, // auto-dismiss when session actually expires
        },
      );
    }

    // Dismiss warning on any user activity (mousemove fires often — use keydown/click)
    function handleActivity() {
      if (warningIdRef.current) {
        toast.dismiss(warningIdRef.current);
        warningIdRef.current = null;
        dismissedRef.current = false;
      }
    }

    window.addEventListener('pww:session-warning', handleWarning);
    document.addEventListener('keydown', handleActivity, { passive: true });
    document.addEventListener('mousedown', handleActivity, { passive: true });

    return () => {
      window.removeEventListener('pww:session-warning', handleWarning);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('mousedown', handleActivity);
    };
  }, [toast]);

  return null;
}

export default SessionWarningListener;
